/**
 * ResetPresetModal — RESET PRESET confirmation modal for V2 workspace.
 *
 * Cycle 101 sprint-3 Task 3.7 (sprint.md:311).
 *
 * UX contract:
 *   * Confirm modal: "Reset {MODE_LABEL} layout? This removes your
 *     customizations. 30-day recovery available." + Cancel (default) /
 *     Reset layout (destructive) buttons.
 *   * Controlled component: caller owns `open` state. V2 shell wires
 *     `ModeIdentityStrip.onResetPreset` to set `open=true`, then this
 *     modal's Confirm calls `onConfirm` + `onClose`.
 *   * `onConfirm` invokes the caller's `resetToPreset()`. Reset is
 *     per-mode scoped — `resetToPreset()` only touches the active mode's
 *     storage keys (AC-8 sprint-3).
 *   * Closes on Cancel, ESC, or backdrop click.
 *
 * The modal does NOT own the layout — it receives `onConfirm` from the
 * caller who holds the `useModeCanvasLayout` instance.
 */

import { useEffect } from 'react';

export interface ResetPresetModalProps {
  readonly open: boolean;
  /** Operator-facing mode label — e.g., "WORLD". */
  readonly modeLabel: string;
  /** Cancel / ESC / backdrop — request modal close. */
  readonly onClose: () => void;
  /** Confirm — wire `useModeCanvasLayout.resetToPreset()` here. */
  readonly onConfirm: () => void;
}

export function ResetPresetModal({
  open,
  modeLabel,
  onClose,
  onConfirm,
}: ResetPresetModalProps) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reset-preset-heading"
      data-testid="reset-preset-modal"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(4, 6, 10, 0.78)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 70,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 440,
          maxWidth: '92vw',
          background: 'var(--cd-bg-raised)',
          border: '1px solid var(--cd-border-strong)',
          borderRadius: 'var(--cd-radius)',
          boxShadow: 'var(--cd-shadow-2)',
          padding: 24,
          fontFamily: 'var(--cd-font-sans)',
          color: 'var(--cd-fg)',
        }}
      >
        <h2
          id="reset-preset-heading"
          style={{
            margin: 0,
            fontFamily: 'var(--cd-font-mono)',
            fontSize: 12,
            letterSpacing: '0.14em',
            fontWeight: 500,
            textTransform: 'uppercase',
            color: 'var(--cd-fg)',
          }}
        >
          Reset {modeLabel} layout?
        </h2>
        <p
          style={{
            margin: '12px 0 20px',
            fontSize: 13,
            lineHeight: 1.5,
            color: 'var(--cd-fg-secondary)',
          }}
        >
          This removes your customizations for the {modeLabel} mode.
          30-day recovery available — your current layout moves to the
          recycle tier and can be restored from there.
        </p>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            autoFocus
            style={{
              padding: '8px 14px',
              fontFamily: 'var(--cd-font-mono)',
              fontSize: 10.5,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              fontWeight: 500,
              color: 'var(--cd-fg)',
              background: 'transparent',
              border: '1px solid var(--cd-border-strong)',
              borderRadius: 'var(--cd-radius)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            aria-label={`Confirm reset ${modeLabel} layout`}
            style={{
              padding: '8px 14px',
              fontFamily: 'var(--cd-font-mono)',
              fontSize: 10.5,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: 'var(--cd-bg)',
              background: 'var(--cd-sig-ember)',
              border: 'none',
              borderRadius: 'var(--cd-radius)',
              cursor: 'pointer',
            }}
          >
            Reset layout
          </button>
        </div>
      </div>
    </div>
  );
}
